import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  bannersUseCloudDirectly,
  createCloudBanner,
  getCloudBanner,
  listCloudBanners,
  updateCloudBanner
} from '../services/cloudBannersClient';
import { subscribeCloudRefresh } from '../services/cloudRealtimeClient';
import '../styles/NivoraPortalUtilities.css';

const initialForm = {
  titulo: '',
  descricao: '',
  tipo: 'Publicidade',
  parceiro: '',
  imagem_url: '',
  imagem_base64: '',
  cta_texto: 'Conhecer oferta',
  link: '',
  prioridade: '0',
  abrir_nova_aba: 1,
  data_inicio: '',
  data_fim: '',
  ativo: 1
};

function today() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

function normalize(value) {
  return value === '' ? null : value;
}

function bannerVigencia(banner, referenceDate = today()) {
  const start = String(banner?.data_inicio || '');
  const end = String(banner?.data_fim || '');
  if (start && start > referenceDate) return 'agendada';
  if (end && end < referenceDate) return 'encerrada';
  return 'vigente';
}

function matchesVigencia(banner, filter) {
  if (!filter) return true;
  if (filter === 'sem_fim') return !banner.data_fim;
  return bannerVigencia(banner) === filter;
}

function cloudPayload(formData, active = formData.ativo) {
  return {
    titulo: String(formData.titulo || '').trim(),
    descricao: normalize(formData.descricao),
    tipo: formData.tipo || 'Publicidade',
    parceiro: normalize(formData.parceiro),
    imagem_url: normalize(formData.imagem_url),
    imagem_base64: normalize(formData.imagem_base64),
    cta_texto: normalize(formData.cta_texto),
    link: normalize(formData.link),
    prioridade: Number(formData.prioridade || 0),
    abrir_nova_aba: Boolean(Number(formData.abrir_nova_aba || 0)),
    ativo: Boolean(Number(active || 0)),
    data_inicio: normalize(formData.data_inicio),
    data_fim: normalize(formData.data_fim)
  };
}

export default function BannersParceiros() {
  const cloudDirect = bannersUseCloudDirectly();
  const [banners, setBanners] = useState([]);
  const [formData, setFormData] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [editingVersion, setEditingVersion] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [vigenciaFilter, setVigenciaFilter] = useState('');

  const resumo = useMemo(() => ({
    total: banners.length,
    ativos: banners.filter((item) => Number(item.ativo || 0) === 1).length,
    comLink: banners.filter((item) => item.link).length,
    comImagem: banners.filter((item) => item.imagem_url || item.imagem_base64 || Number(item.tem_imagem_base64 || 0) === 1).length
  }), [banners]);

  const filteredBanners = useMemo(() => banners.filter((banner) => {
    const matchesStatus = !statusFilter
      || (statusFilter === 'ativo' && Number(banner.ativo || 0) === 1)
      || (statusFilter === 'pausado' && Number(banner.ativo || 0) !== 1);
    return matchesStatus && matchesVigencia(banner, vigenciaFilter);
  }), [banners, statusFilter, vigenciaFilter]);

  const loadBanners = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = cloudDirect
        ? (await listCloudBanners())?.items
        : await window.electron.db.query(
            `SELECT id, titulo, descricao, tipo, parceiro, imagem_url, cta_texto, link,
                    prioridade, abrir_nova_aba, ativo, data_inicio, data_fim, data_criacao,
                    CASE WHEN COALESCE(imagem_base64, '') <> '' THEN 1 ELSE 0 END AS tem_imagem_base64
             FROM dashboard_banners
             ORDER BY ativo DESC, prioridade DESC, data_criacao DESC`
          );
      setBanners(data || []);
    } catch (loadError) {
      setError(loadError.message || 'Não foi possível carregar os banners.');
    } finally {
      setLoading(false);
    }
  }, [cloudDirect]);

  useEffect(() => {
    void loadBanners();
  }, [loadBanners]);

  useEffect(() => {
    if (!cloudDirect) return undefined;
    return subscribeCloudRefresh(
      ['dashboard_banners'],
      () => { void loadBanners(); },
      { delayMs: 300 }
    );
  }, [cloudDirect, loadBanners]);

  const resetForm = () => {
    setFormData(initialForm);
    setEditingId(null);
    setEditingVersion('');
    setShowForm(false);
    setMessage('');
    setError('');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value }));
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setFormData((prev) => ({ ...prev, imagem_base64: reader.result, imagem_url: '' }));
    };
    reader.readAsDataURL(file);
  };

  const salvar = async (e) => {
    e.preventDefault();
    const titulo = String(formData.titulo || '').trim();
    if (!titulo) return alert('Informe o título do banner.');

    const values = [
      titulo,
      normalize(formData.descricao),
      formData.tipo || 'Publicidade',
      normalize(formData.parceiro),
      normalize(formData.imagem_url),
      normalize(formData.imagem_base64),
      normalize(formData.cta_texto),
      normalize(formData.link),
      Number(formData.prioridade || 0),
      Number(formData.abrir_nova_aba ? 1 : 0),
      normalize(formData.data_inicio),
      normalize(formData.data_fim)
    ];

    setBusy('save');
    setError('');
    try {
      if (cloudDirect) {
        const payload = cloudPayload(formData);
        if (editingId) {
          await updateCloudBanner(editingId, editingVersion, payload);
          setMessage('Banner atualizado.');
        } else {
          await createCloudBanner(payload);
          setMessage('Banner cadastrado.');
        }
      } else if (editingId) {
        await window.electron.db.run(
          `UPDATE dashboard_banners
           SET titulo = ?, descricao = ?, tipo = ?, parceiro = ?, imagem_url = ?, imagem_base64 = ?,
               cta_texto = ?, link = ?, prioridade = ?, abrir_nova_aba = ?, data_inicio = ?, data_fim = ?
           WHERE id = ?`,
          [...values, editingId]
        );
        setMessage('Banner atualizado.');
      } else {
        await window.electron.db.run(
          `INSERT INTO dashboard_banners (
             titulo, descricao, tipo, parceiro, imagem_url, imagem_base64,
             cta_texto, link, prioridade, abrir_nova_aba, data_inicio, data_fim
           ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          values
        );
        setMessage('Banner cadastrado.');
      }

      await loadBanners();
      setFormData(initialForm);
      setEditingId(null);
      setEditingVersion('');
      setShowForm(false);
    } catch (saveError) {
      setError(saveError.message || 'Não foi possível salvar o banner.');
    } finally {
      setBusy('');
    }
  };

  const editar = async (banner) => {
    setBusy(`edit-${banner.id}`);
    setError('');
    let detail = banner;
    try {
      if (cloudDirect) {
        detail = (await getCloudBanner(banner.id))?.banner || banner;
      } else {
        const data = await window.electron.db.query(
          'SELECT * FROM dashboard_banners WHERE id = ? LIMIT 1',
          [banner.id]
        );
        if (Array.isArray(data)) {
          detail = data.find((item) => Number(item.id) === Number(banner.id)) || data[0] || banner;
        }
      }
    } catch (editError) {
      setError(editError.message || 'Não foi possível carregar os detalhes do banner.');
      setBusy('');
      return;
    }
    setEditingId(banner.id);
    setEditingVersion(String(detail.version || banner.version || ''));
    setFormData({
      titulo: detail.titulo || '',
      descricao: detail.descricao || '',
      tipo: detail.tipo || 'Publicidade',
      parceiro: detail.parceiro || '',
      imagem_url: detail.imagem_url || '',
      imagem_base64: detail.imagem_base64 || '',
      cta_texto: detail.cta_texto || 'Conhecer oferta',
      link: detail.link || '',
      prioridade: String(detail.prioridade || 0),
      abrir_nova_aba: Number(detail.abrir_nova_aba || 0) === 1 ? 1 : 0,
      data_inicio: detail.data_inicio || '',
      data_fim: detail.data_fim || '',
      ativo: Number(detail.ativo ?? banner.ativo ?? 1) === 1 ? 1 : 0
    });
    setShowForm(true);
    setBusy('');
  };

  const alternarStatus = async (banner) => {
    setBusy(`status-${banner.id}`);
    setError('');
    try {
      if (cloudDirect) {
        const detail = (await getCloudBanner(banner.id))?.banner;
        if (!detail) throw new Error('Banner não encontrado.');
        await updateCloudBanner(
          banner.id,
          detail.version || banner.version,
          cloudPayload(detail, Number(banner.ativo || 0) === 1 ? 0 : 1)
        );
      } else {
        await window.electron.db.run(
          'UPDATE dashboard_banners SET ativo = ? WHERE id = ?',
          [Number(banner.ativo || 0) === 1 ? 0 : 1, banner.id]
        );
      }
      await loadBanners();
      setMessage(Number(banner.ativo || 0) === 1 ? 'Banner pausado.' : 'Banner ativado.');
    } catch (statusError) {
      setError(statusError.message || 'Não foi possível alterar o status do banner.');
    } finally {
      setBusy('');
    }
  };

  return (
    <main className="nivora-portal nivora-portal--partners nivora-banner-page" aria-busy={loading || Boolean(busy)}>
      <header className="nivora-portal__hero" aria-labelledby="partner-banners-title">
        <div className="nivora-portal__hero-copy">
          <span className="nivora-portal__eyebrow"><i className="fas fa-user-shield" aria-hidden="true" /> Gestão exclusiva do proprietário</span>
          <h1 id="partner-banners-title"><span className="nivora-portal__hero-icon" aria-hidden="true"><i className="fas fa-images" /></span> Central de Novidades</h1>
          <p>Organize comunicados do sistema e campanhas de parceiros exibidas no carrossel da home do ERP.</p>
          <div className="nivora-portal__hero-tags" aria-label="Controles da central">
            <span><i className="fas fa-lock" aria-hidden="true" /> Owner-only</span>
            <span><i className="fas fa-calendar-alt" aria-hidden="true" /> Vigência programada</span>
            <span><i className="fas fa-sort-amount-up" aria-hidden="true" /> Priorização</span>
          </div>
        </div>
        <div className="nivora-portal__hero-actions">
          <div className="nivora-portal__hero-state is-violet">
            <i className="fas fa-bullhorn" aria-hidden="true" />
            <span><strong>{resumo.ativos} campanha(s) ativa(s)</strong><small>{resumo.total} banner(s) sob gestão</small></span>
          </div>
          <button type="button" className="btn btn-primary" aria-expanded={showForm} aria-controls="partner-banner-form" onClick={() => setShowForm(!showForm)}>
            <i className={`fas ${showForm ? 'fa-times' : 'fa-plus'}`} aria-hidden="true" /> {showForm ? 'Fechar formulário' : 'Novo banner'}
          </button>
        </div>
      </header>

      <section className="nivora-portal__metrics" aria-label="Resumo dos banners">
        <Stat metric="partner-total" icon="fa-images" label="Banners" value={resumo.total} tone="blue" detail="Campanhas cadastradas" />
        <Stat metric="partner-active" icon="fa-check-circle" label="Ativos" value={resumo.ativos} tone="green" detail="Visíveis conforme vigência" />
        <Stat metric="partner-links" icon="fa-link" label="Com link" value={resumo.comLink} tone="cyan" detail="Com chamada externa" />
        <Stat metric="partner-images" icon="fa-image" label="Com imagem" value={resumo.comImagem} tone="violet" detail="Com peça visual" />
      </section>

      <div className="nivora-portal__notice nivora-portal__notice--info">
        <i className="fas fa-shield-alt" aria-hidden="true" />
        <span>Esta rota permanece protegida para o <strong>owner do tenant</strong>. Os aliases da Central de Novidades e Banners Parceiros continuam apontando para a mesma gestão; imagens incorporadas são carregadas somente ao editar.</span>
      </div>

      {message && <div className="alert alert-success" role="status"><i className="fas fa-check-circle" aria-hidden="true" /> {message}</div>}
      {error && <div className="alert alert-danger" role="alert"><i className="fas fa-exclamation-triangle" aria-hidden="true" /> {error}</div>}

      {showForm && (
        <section id="partner-banner-form" className="card nivora-banner-form" aria-labelledby="partner-banner-form-title">
          <div className="card-header"><i className="fas fa-ad" aria-hidden="true"></i> <span id="partner-banner-form-title">{editingId ? 'Editar banner' : 'Novo banner da central'}</span></div>
          <div className="card-body">
            <form onSubmit={salvar} aria-busy={busy === 'save'}>
              <div className="form-row">
                <Input label="Título *" name="titulo" value={formData.titulo} onChange={handleChange} required />
                <Input label="Parceiro/anunciante" name="parceiro" value={formData.parceiro} onChange={handleChange} />
                <Input label="Tipo" name="tipo" value={formData.tipo} onChange={handleChange} />
              </div>
              <Textarea label="Descrição da oferta" name="descricao" value={formData.descricao} onChange={handleChange} />
              <div className="form-row">
                <Input label="URL da imagem" name="imagem_url" value={formData.imagem_url} onChange={handleChange} placeholder="https://..." />
                <div className="form-group">
                  <label className="form-label" htmlFor="partner-banner-file">Upload da imagem</label>
                  <input id="partner-banner-file" name="imagem_arquivo" type="file" className="form-control" accept="image/*" onChange={handleFile} />
                </div>
              </div>
              {(formData.imagem_base64 || formData.imagem_url) && (
                <div className="home-banner-preview nivora-banner-preview" role="img" aria-label={`Prévia do banner ${formData.titulo || 'sem título'}`} style={{ backgroundImage: `url("${formData.imagem_base64 || formData.imagem_url}")` }} />
              )}
              <div className="form-row">
                <Input label="Texto do botão" name="cta_texto" value={formData.cta_texto} onChange={handleChange} />
                <Input label="Link do parceiro" name="link" value={formData.link} onChange={handleChange} placeholder="https://..." />
                <Input label="Prioridade" type="number" name="prioridade" value={formData.prioridade} onChange={handleChange} />
              </div>
              <div className="form-row">
                <Input label="Inicio" type="date" name="data_inicio" value={formData.data_inicio} onChange={handleChange} />
                <Input label="Fim" type="date" name="data_fim" value={formData.data_fim} onChange={handleChange} />
                <div className="form-group nivora-banner-checkbox">
                  <label htmlFor="partner-banner-new-tab"><input id="partner-banner-new-tab" type="checkbox" name="abrir_nova_aba" checked={Number(formData.abrir_nova_aba || 0) === 1} onChange={handleChange} /> Abrir link em nova aba</label>
                </div>
              </div>
              <div className="nivora-banner-form-actions">
                <button type="submit" className="btn btn-success" disabled={busy === 'save'}><i className={`fas ${busy === 'save' ? 'fa-spinner fa-spin' : 'fa-save'}`}></i> Salvar banner</button>
                <button type="button" className="btn btn-secondary" onClick={resetForm}><i className="fas fa-times"></i> Cancelar</button>
              </div>
            </form>
          </div>
        </section>
      )}

      <section className="card nivora-banner-list" aria-labelledby="partner-banner-list-title">
        <div className="card-header"><i className="fas fa-list" aria-hidden="true"></i> <span id="partner-banner-list-title">Banners da central</span></div>
        <div className="card-body nivora-banner-table-scroll">
          <div className="form-row" aria-label="Filtros dos banners">
            <div className="form-group">
              <label className="form-label" htmlFor="partner-banner-status-filter">Status</label>
              <select id="partner-banner-status-filter" name="banner_status" className="form-select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option value="">Todos</option>
                <option value="ativo">Ativos</option>
                <option value="pausado">Pausados</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="partner-banner-vigencia-filter">Vigência</label>
              <select id="partner-banner-vigencia-filter" name="banner_vigencia" className="form-select" value={vigenciaFilter} onChange={(event) => setVigenciaFilter(event.target.value)}>
                <option value="">Todas</option>
                <option value="vigente">Vigentes hoje</option>
                <option value="agendada">Agendadas</option>
                <option value="encerrada">Encerradas</option>
                <option value="sem_fim">Sem data final</option>
              </select>
            </div>
            {(statusFilter || vigenciaFilter) && (
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => {
                  setStatusFilter('');
                  setVigenciaFilter('');
                }}
              >
                <i className="fas fa-eraser" aria-hidden="true" /> Limpar filtros
              </button>
            )}
          </div>
          {loading ? (
            <div className="nivora-portal__empty" role="status"><span className="nivora-portal__empty-icon"><i className="fas fa-spinner fa-spin" /></span><div><strong>Carregando campanhas</strong><p>Consultando os banners cadastrados neste tenant.</p></div></div>
          ) : filteredBanners.length === 0 ? (
            <div className="nivora-portal__empty" role="status"><span className="nivora-portal__empty-icon"><i className="fas fa-images" /></span><div><strong>{banners.length ? 'Nenhum banner corresponde aos filtros' : 'Nenhum banner cadastrado'}</strong><p>{banners.length ? 'Altere o status ou a vigência selecionada.' : 'Crie a primeira novidade ou campanha de parceiro.'}</p></div></div>
          ) : (
            <table className="table">
              <caption>Banners cadastrados na central de novidades</caption>
              <thead>
                <tr>
                  <th>Banner</th>
                  <th>Parceiro</th>
                  <th>Período</th>
                  <th>Prioridade</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredBanners.map((banner) => (
                  <tr key={banner.id}>
                    <td data-label="Banner"><strong>{banner.titulo}</strong><br /><small>{banner.cta_texto || '-'} {banner.link ? `- ${banner.link}` : ''}</small></td>
                    <td data-label="Parceiro">{banner.parceiro || '-'}</td>
                    <td data-label="Período">{banner.data_inicio || 'início imediato'} até {banner.data_fim || 'sem fim'}<br /><small>{vigenciaLabel(bannerVigencia(banner))}</small></td>
                    <td data-label="Prioridade">{banner.prioridade || 0}</td>
                    <td data-label="Status"><span className={`badge ${Number(banner.ativo || 0) === 1 ? 'badge-success' : 'badge-warning'}`}>{Number(banner.ativo || 0) === 1 ? 'ativo' : 'pausado'}</span></td>
                    <td data-label="Ações">
                      <div className="nivora-banner-row-actions">
                        <button type="button" className="btn btn-sm btn-outline" disabled={busy === `edit-${banner.id}`} aria-label={`Editar banner ${banner.titulo}`} onClick={() => editar(banner)}><i className={`fas ${busy === `edit-${banner.id}` ? 'fa-spinner fa-spin' : 'fa-edit'}`}></i></button>
                        <button type="button" className="btn btn-sm btn-secondary" disabled={busy === `status-${banner.id}`} aria-label={`${Number(banner.ativo || 0) === 1 ? 'Pausar' : 'Ativar'} banner ${banner.titulo}`} onClick={() => alternarStatus(banner)}>
                          <i className={`fas ${Number(banner.ativo || 0) === 1 ? 'fa-pause' : 'fa-play'}`}></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </main>
  );
}

function Stat({ metric, icon, label, value, detail, tone }) {
  return (
    <article className={`nivora-portal__metric nivora-portal__metric--${tone}`} data-metric={metric}>
      <span className="nivora-portal__metric-icon" aria-hidden="true"><i className={`fas ${icon}`}></i></span>
      <span className="nivora-portal__metric-copy"><small>{label}</small><strong>{value}</strong><span>{detail}</span></span>
    </article>
  );
}

function vigenciaLabel(value) {
  if (value === 'agendada') return 'Agendada';
  if (value === 'encerrada') return 'Encerrada';
  return 'Vigente';
}

function Input({ label, type = 'text', ...props }) {
  const inputId = props.id || `partner-banner-${props.name}`;
  return (
    <div className="form-group">
      <label className="form-label" htmlFor={inputId}>{label}</label>
      <input id={inputId} type={type} className="form-control" {...props} />
    </div>
  );
}

function Textarea({ label, ...props }) {
  const inputId = props.id || `partner-banner-${props.name}`;
  return (
    <div className="form-group">
      <label className="form-label" htmlFor={inputId}>{label}</label>
      <textarea id={inputId} className="form-control" rows="3" {...props} />
    </div>
  );
}
